/**
 * Shared admin form primitives + data-source metadata.
 *
 * Reused across the client list, new-client wizard, and client detail tabs so
 * the admin section stays visually consistent and the Windsor source lookup /
 * config-field rules live in exactly one place.
 */
import { WINDSOR_SOURCE_BY_TYPE } from "@prism/shared/constants";

export function SectionLabel({ children }) {
  return (
    <div className="text-[11px] uppercase text-text-muted font-semibold mb-2.5" style={{ letterSpacing: "0.5px" }}>
      {children}
    </div>
  );
}

export function FieldLabel({ children }) {
  return (
    <label className="block text-[10px] uppercase text-text-muted font-medium mb-1" style={{ letterSpacing: "0.5px" }}>
      {children}
    </label>
  );
}

export function FieldError({ children }) {
  return <div className="text-[11px] text-negative mt-1">{children}</div>;
}

export function Field({ label, type = "text", value, onChange, required, placeholder, autoFocus, error, help, mono }) {
  return (
    <div>
      {label && (
        <FieldLabel>
          {label}
          {required && <span className="text-negative ml-0.5">*</span>}
        </FieldLabel>
      )}
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        autoFocus={autoFocus}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-9 px-3 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors ${mono ? "font-mono" : ""}`}
      />
      {error && <FieldError>{error}</FieldError>}
      {help && !error && <div className="text-[10px] text-text-muted mt-1">{help}</div>}
    </div>
  );
}

export function Select({ label, value, onChange, options, required, error }) {
  return (
    <div>
      {label && (
        <FieldLabel>
          {label}
          {required && <span className="text-negative ml-0.5">*</span>}
        </FieldLabel>
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 px-2.5 rounded border border-border-default text-[13px] text-text-primary bg-surface focus:outline-none focus:border-accent transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}

// Source type → Windsor source id (auto-populated when a type is selected).
// Canonical map lives in @prism/shared; re-exported here under the name the
// admin forms already use.
export const WINDSOR_SOURCE_MAP = WINDSOR_SOURCE_BY_TYPE;

export const SOURCE_TYPES = [
  { value: "ga4",                     label: "Google Analytics 4" },
  { value: "google_search_console",   label: "Google Search Console" },
  { value: "google_ads",              label: "Google Ads" },
  { value: "shopify",                 label: "Shopify" },
  { value: "meta_ads",                label: "Meta Ads" },
  { value: "linkedin_ads",            label: "LinkedIn Ads" },
  { value: "youtube_analytics",       label: "YouTube Analytics" },
  { value: "google_business_profile", label: "Google Business Profile" },
  { value: "metricool",               label: "Metricool (social)" },
];

// Per-type extra config field surfaced in source forms (key stored in config).
export const SOURCE_CONFIG_FIELD = {
  ga4:                   { key: "propertyId", label: "GA4 Property ID", placeholder: "477021610" },
  google_search_console: { key: "siteUrl",    label: "Site URL",        placeholder: "https://example.com" },
  google_ads:            { key: "customerId", label: "Customer ID",     placeholder: "123-456-7890" },
  shopify:               { key: "shopDomain", label: "Shop Domain",     placeholder: "myshop.myshopify.com" },
};

// Default field template per source type (pre-selected in the wizard).
export const DEFAULT_TEMPLATE = {
  ga4:                     "GA4 Standard",
  google_search_console:   "Google Search Console Standard",
  google_ads:              "Google Ads Standard",
  shopify:                 "Shopify Standard",
  meta_ads:                "Meta Ads Standard",
  google_business_profile: "Google Business Profile Standard",
  youtube_analytics:       "YouTube Analytics Standard",
};

/**
 * Derive an overall client status from its data sources. No persisted
 * "syncing" state exists, so we map to Active / Error / Not synced.
 */
export function clientStatus(dataSources = []) {
  const live = dataSources.filter((d) => d.status !== "disconnected");
  if (live.some((d) => d.status === "error"))                     return { label: "Error",      dot: "#dc2626" };
  if (live.some((d) => d.status === "connected" && d.lastSync))   return { label: "Active",     dot: "#16a34a" };
  return { label: "Not synced", dot: "#a3a3a3" };
}

export function StatusChip({ dataSources }) {
  const s = clientStatus(dataSources);
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] text-text-secondary">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.dot }} />
      {s.label}
    </span>
  );
}

export function SourceChip({ children }) {
  return (
    <span className="inline-flex items-center h-5 px-2 rounded bg-app-bg border border-border-default text-[10px] text-text-secondary font-mono">
      {children}
    </span>
  );
}
